import Phaser from 'phaser';
import { GAME_CONFIG } from './config';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { HomeScene } from './scenes/HomeScene';
import { StageSelectScene } from './scenes/StageSelectScene';
import { ClosetScene } from './scenes/ClosetScene';
import { GameScene } from './scenes/GameScene';
import { ResultScene } from './scenes/ResultScene';
import { SettingsScene } from './scenes/SettingsScene';
import { stageProgress } from './state/StageProgress';
import * as Telemetry from './services/Telemetry';
import * as RemoteConfig from './services/RemoteConfig';
import * as CloudSave from './services/CloudSave';
import * as Messaging from './services/Messaging';
import { bootAnonymous } from './services/AuthService';
import { appSettings } from './services/AppSettings';

// AdMob standard banner is 320×50 dp. On a typical 2× DPI phone that maps to
// about 50 CSS px; we add a few px of breathing room so the canvas top edge
// doesn't sit flush against the ad's bottom border. The Phaser canvas shrinks
// to fit the remaining viewport height — FIT mode + autoCenter keep the rest
// of the layout proportional, so no per-scene shift is needed. When the native
// banner is later wired up (Phase 0 of the roadmap), the AdMob plugin renders
// inside this reserved strip and the canvas stays put.
const AD_BANNER_RESERVE_PX = 56;

/** Reads OS-reported safe-area insets via the CSS custom properties exposed
 * on :root in index.html (which in turn pull from `env(safe-area-inset-*)`).
 *
 * - iOS with Dynamic Island: top ~59pt, bottom 34pt
 * - iOS notched (pre-Dynamic Island): top ~47pt, bottom 34pt
 * - Android edge-to-edge with gesture nav: top ~24dp status bar, bottom ~24dp
 * - Android 3-button nav: bottom ~48dp
 * - Desktop / non-notched: 0 / 0 (env defaults)
 *
 * Reading via CSS env() instead of hardcoded numbers means the OS handles
 * orientation flips, nav-mode toggles, and foldable hinge state for us. */
function readSafeAreaInsets() {
  const cs = getComputedStyle(document.documentElement);
  const parse = (v: string) => parseFloat(v) || 0;
  return {
    top: parse(cs.getPropertyValue('--safe-top')),
    bottom: parse(cs.getPropertyValue('--safe-bottom')),
  };
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'app',
  width: GAME_CONFIG.STAGE_W,
  height: GAME_CONFIG.STAGE_H,
  // Matches COLORS.ink — the letterbox color also defaults to this during
  // Boot/Preload, so first-paint flicker is invisible. Scenes that need a
  // different tone (Home/Closet/Result use cream) flip the letterbox via
  // setLetterboxColor in their drawBackdrop.
  backgroundColor: '#1a0826',
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  // Explicit render config: Android browsers (Samsung Internet especially)
  // pick conservative defaults that can show as muddy character art and
  // janky frames. powerPreference asks the OS for the faster GPU when one
  // is available, antialias keeps the bold outline shapes clean, and
  // pixelArt:false plus roundPixels:false keep the rotated head sampled
  // with linear filtering instead of falling back to nearest.
  render: {
    antialias: true,
    pixelArt: false,
    roundPixels: false,
    powerPreference: 'high-performance',
    premultipliedAlpha: true,
  },
  fps: {
    target: 60,
    forceSetTimeOut: false,
  },
  input: {
    activePointers: 2,
  },
  scene: [BootScene, PreloadScene, HomeScene, StageSelectScene, ClosetScene, GameScene, ResultScene, SettingsScene],
};

const game = new Phaser.Game(config);

// Pre-seed safe area values synchronously before any scene's create() runs.
// fitAppToVisualViewport runs via requestAnimationFrame (next frame), but
// BootScene.create() may fire on the same frame as game construction.
{
  const cap = (window as any).Capacitor;
  const native = cap && (typeof cap.isNativePlatform === 'function'
    ? cap.isNativePlatform()
    : (cap.getPlatform?.() ?? cap.platform) === 'ios' || (cap.getPlatform?.() ?? cap.platform) === 'android');
  if (native) {
    const sw = window.screen.width;
    const sh = window.screen.height;
    const cssScale = Math.min(sw / GAME_CONFIG.STAGE_W, sh / GAME_CONFIG.STAGE_H);
    const safeTop = sh >= 852 ? 79 : sh >= 812 ? 67 : 0;
    const safeBottom = sh >= 812 ? 34 : 0;
    game.registry.set('safeInsetCssTop', safeTop);
    game.registry.set('safeInsetCssBottom', safeBottom);
    game.registry.set('canvasPerCssPx', cssScale > 0 ? 1 / cssScale : 1);
  }
}

if (import.meta.env.DEV) {
  (window as unknown as { __game: Phaser.Game }).__game = game;
}

// CSS-only viewport sizing (100dvh) wasn't enough on iOS Chrome: position:fixed
// stays anchored to the layout viewport (full screen, ignoring URL bar +
// bottom toolbar), so the parent ends up bigger than the visible region and
// Phaser's FIT mode leaves an asymmetric black band. visualViewport reports
// the actual visible region in pixels and updates live as the URL bar
// shows/hides — we mirror those numbers onto #app's inline styles, then ask
// Phaser to re-fit. CSS fallback (100vw / 100dvh / 100vh) still covers first
// paint before this runs and browsers without visualViewport.
//
// Earlier revision also mirrored visualViewport.offsetTop/Left onto #app's
// top/left. iOS Chrome was reporting a small positive offsetLeft, which
// pushed #app to the right and produced a visible left-side black band. The
// URL bar on iOS Chrome sits OUTSIDE the WebView frame (visualViewport's
// origin is always at the viewport's top-left), so we don't actually need
// the offset — just the size.
let safeAreaEmitTimer: number | null = null;
function scheduleSafeAreaEmit() {
  // visualViewport.scroll fires every animation frame while iOS Chrome's URL
  // bar is sliding — that's 20+ events per second. Debounce so scenes only
  // restart once the gesture settles, otherwise the cream backdrop flickers.
  if (safeAreaEmitTimer != null) window.clearTimeout(safeAreaEmitTimer);
  safeAreaEmitTimer = window.setTimeout(() => {
    safeAreaEmitTimer = null;
    game.events.emit('safeAreaChanged');
  }, 120);
}

function isNativeApp(): boolean {
  const cap = (window as any).Capacitor;
  if (!cap) return false;
  if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
  const platform = typeof cap.getPlatform === 'function' ? cap.getPlatform() : cap.platform;
  return platform === 'ios' || platform === 'android';
}

function fitAppToVisualViewport() {
  const app = document.getElementById('app');
  if (!app) return;
  const vv = window.visualViewport;
  const width = vv ? vv.width : window.innerWidth;
  const height = vv ? vv.height : window.innerHeight;

  const native = isNativeApp();
  const safe = readSafeAreaInsets();

  if (native) {
    app.style.position = 'fixed';
    app.style.inset = '0';
    app.style.width = '';
    app.style.height = '';
    game.scale.refresh();

    const w = app.clientWidth || width;
    const h = app.clientHeight || height;
    const cssScale = Math.min(w / GAME_CONFIG.STAGE_W, h / GAME_CONFIG.STAGE_H);
    const canvasPerCssPx = cssScale > 0 ? 1 / cssScale : 1;

    // env(safe-area-inset-*) returns 0 with contentInset:'never'.
    // Fall back to screen-height heuristic for iOS devices.
    let safeTop = safe.top;
    let safeBottom = safe.bottom;
    if (safeTop === 0 && window.screen.height >= 812) {
      const sh = window.screen.height;
      safeTop = sh >= 852 ? 79 : 67;   // Dynamic Island : notch
      safeBottom = 34;                   // home indicator
    }

    game.registry.set('safeInsetCssTop', safeTop);
    game.registry.set('safeInsetCssBottom', safeBottom);
    game.registry.set('canvasPerCssPx', canvasPerCssPx);
    scheduleSafeAreaEmit();
    return;
  }

  const topReserve = Math.max(AD_BANNER_RESERVE_PX, Math.floor(safe.top));
  const bottomReserve = Math.floor(safe.bottom);
  const usable = Math.max(100, height - topReserve - bottomReserve);

  app.style.width = `${width}px`;
  app.style.height = `${usable}px`;
  app.style.top = `${topReserve}px`;
  app.style.left = '0px';
  game.scale.refresh();

  const cssScale = Math.min(width / GAME_CONFIG.STAGE_W, usable / GAME_CONFIG.STAGE_H);
  const canvasPerCssPx = cssScale > 0 ? 1 / cssScale : 1;
  game.registry.set('safeInsetCssTop', topReserve);
  game.registry.set('safeInsetCssBottom', bottomReserve);
  game.registry.set('canvasPerCssPx', canvasPerCssPx);
  scheduleSafeAreaEmit();
}

// Initial sizing — run on the next frame so layout has actually settled
// (visualViewport.width can lie on the very first paint on iOS).
requestAnimationFrame(fitAppToVisualViewport);

// Boot all backend/state services. Local stores (stageProgress, appSettings)
// load synchronously since scene create() may consume them on the same frame.
// External services (Telemetry/RemoteConfig/Auth/CloudSave/Messaging) run
// async — each fails soft so the game proceeds even when Firebase/AdMob keys
// are missing. CloudSave + Messaging are still stubs in Phase α; calling them
// here locks the boot call graph in ahead of Phase δ.
async function bootServices() {
  stageProgress.load();
  appSettings.load();

  // independent boots — no inter-dependency
  const [, user] = await Promise.all([
    Telemetry.init().catch((err) => console.warn('Telemetry.init', err)),
    bootAnonymous().catch((err) => { console.warn('bootAnonymous', err); return null; }),
    RemoteConfig.fetchAndActivate().catch((err) => console.warn('RemoteConfig', err)),
    Messaging.bootSilent().catch((err) => console.warn('Messaging.bootSilent', err)),
  ]);

  if (user) {
    await Telemetry.setUserId(user.uid).catch((err) => console.warn('Telemetry.setUserId', err));
  }
  await CloudSave.bootSync().catch((err) => console.warn('CloudSave.bootSync', err));
  await Telemetry.logEvent('app_boot', { build: import.meta.env.MODE })
    .catch((err) => console.warn('Telemetry.logEvent app_boot', err));
}

bootServices().catch((err) => console.warn('bootServices', err));

window.addEventListener('resize', fitAppToVisualViewport);
window.addEventListener('orientationchange', fitAppToVisualViewport);
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', fitAppToVisualViewport);
  // `scroll` on visualViewport fires while the URL bar is animating in/out,
  // so the canvas tracks smoothly during that transition.
  window.visualViewport.addEventListener('scroll', fitAppToVisualViewport);
}
